import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react"
import { useEffect, useState } from "react"

type LicenseLimitEventPayload = {
  code?: string
  error?: string
  limit_key?: string
  current_count?: number
  max_allowed?: number
  max_per_day?: number
  used_today?: number
  required_plan?: string
  current_plan?: string
  feature_key?: string
  reason?: string
  license_plan?: string
  license_status?: string
  operator_hint?: string
  status?: number
  statusText?: string
}

const limitLabels: Record<string, string> = {
  max_active_users: "aktywne konta użytkowników",
  max_warehouses: "aktywne magazyny",
  max_product_label_templates: "aktywne szablony etykiet produktów",
  documents_per_day: "dokumenty na dzień",
  exports_per_day: "eksporty na dzień",
}

const featureLabels: Record<string, string> = {
  technology_management: "zarządzanie technologiami",
  production_orders: "zlecenia produkcyjne",
  auto_replenishment: "automatyczne uzupełnianie",
  inventory_count: "inwentaryzacja",
  advanced_reports: "raporty rozszerzone",
  operational_analytics: "analityka operacyjna",
  product_labels: "etykiety produktów",
  ksef_integration: "integracja KSeF",
}

function buildMessage(payload: LicenseLimitEventPayload): string {
  const limitLabel = limitLabels[payload.limit_key ?? ""] ?? payload.limit_key
  if (
    payload.code === "LICENSE_LIMIT_DAILY_REACHED" ||
    payload.error === "daily license limit reached"
  ) {
    const max = payload.max_per_day ?? payload.max_allowed
    const used = payload.used_today
    if (max && used) {
      return `Osiągnięto dzienny limit (${used}/${max}) dla: ${limitLabel}.`
    }
    if (max) {
      return `Osiągnięto dzienny limit ${max} dla: ${limitLabel}.`
    }
    return `Osiągnięto dzienny limit dla: ${limitLabel}.`
  }
  if (
    payload.code === "LICENSE_LIMIT_RESOURCE_REACHED" ||
    payload.error === "resource license limit reached"
  ) {
    if (
      payload.max_allowed !== undefined &&
      payload.current_count !== undefined
    ) {
      return `Limit licencyjny został osiągnięty (${payload.current_count}/${payload.max_allowed}) dla: ${limitLabel}.`
    }
    if (payload.max_allowed !== undefined) {
      return `Limit licencyjny ${payload.max_allowed} został osiągnięty dla: ${limitLabel}.`
    }
    return `Limit licencyjny został osiągnięty dla: ${limitLabel}.`
  }
  if (
    payload.code === "LICENSE_PLAN_NOT_ALLOWED" ||
    payload.error === "plan is not allowed for this operation"
  ) {
    const feature =
      featureLabels[payload.feature_key ?? ""] ??
      payload.feature_key ??
      "ta funkcja"
    if (payload.reason === "plan_excluded") {
      return `Plan ${payload.current_plan ?? payload.license_plan ?? "BASIC"} nie obejmuje modułu: ${feature}.`
    }
    if (payload.current_plan && payload.required_plan) {
      return `Ta operacja wymaga planu ${payload.required_plan}, a bieżący plan to ${payload.current_plan}.`
    }
    return "Ta operacja nie jest dostępna w bieżącym planie licencyjnym."
  }
  if (
    payload.code === "LICENSE_FEATURE_DISABLED" ||
    payload.error === "feature is disabled by license policy"
  ) {
    const featureLabel =
      featureLabels[payload.feature_key ?? ""] ?? payload.feature_key
    if (payload.reason === "license_override" && featureLabel) {
      return `Moduł "${featureLabel}" jest wyłączony w ustawieniach tej licencji. Administrator może go włączyć, jeśli obejmuje go plan.`
    }
    if (payload.reason === "dependency_disabled" && featureLabel) {
      return `Moduł "${featureLabel}" zależy od innego wyłączonego modułu.`
    }
    if (payload.reason === "invalid_contract") {
      return "Nie można potwierdzić konfiguracji licencji. Skontaktuj się z administratorem."
    }
    if (featureLabel) {
      return `Funkcja "${featureLabel}" jest wyłączona w bieżącej licencji.`
    }
    return "Wybrana funkcja jest wyłączona przez politykę licencji."
  }
  if (
    payload.code === "LICENSE_STATUS_BLOCKED" ||
    payload.error === "license is not active for this operation"
  ) {
    if (payload.license_status) {
      return `Operacja zablokowana przez status licencji: ${payload.license_status}.`
    }
    return "Operacja zablokowana przez nieaktywny status licencji."
  }
  return "Osiągnięto limit licencyjny dla tej operacji."
}

export default function LicenseLimitModal() {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [message, setMessage] = useState("")
  const [hint, setHint] = useState("")
  const [title, setTitle] = useState("Ograniczenie licencji")

  useEffect(() => {
    const listener = (event: Event) => {
      const custom = event as CustomEvent<LicenseLimitEventPayload>
      const payload = custom.detail ?? {}
      setMessage(buildMessage(payload))
      setHint((payload.operator_hint ?? "").trim())
      if (payload.code === "LICENSE_PLAN_NOT_ALLOWED") {
        setTitle("Niedostępne w planie")
      } else if (payload.code === "LICENSE_FEATURE_DISABLED") {
        setTitle("Funkcja niedostępna")
      } else if (
        payload.code === "LICENSE_LIMIT_DAILY_REACHED" ||
        payload.code === "LICENSE_LIMIT_RESOURCE_REACHED"
      ) {
        setTitle("Przekroczono limit")
      } else {
        setTitle("Ograniczenie licencji")
      }
      onOpen()
    }
    window.addEventListener("wms:license-limit", listener)
    return () => {
      window.removeEventListener("wms:license-limit", listener)
    }
  }, [onOpen])

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalBody>
          <Text>{message}</Text>
          {hint && (
            <Text mt={3} color="gray.600" fontSize="sm">
              {hint}
            </Text>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} colorScheme="orange">
            Zamknij
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
